import { SalesCostByCustomer } from "../../../fms.core/BusinessIntelligenceModel";
import { openLoadingWindow, renderReportIntoWindow, buildBroadcastHyperlink, fmtAsOf, NEW_TAB_SLOT } from "./CustomDashboardUtils";

interface MonthCol {
  label: string;
  sub: string;
  salesKeys: string[];
  from: Date;
  to: Date;
  fullTo: Date;
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** 고객 표에 개별로 보여 줄 고객 수. 나머지는 Others 한 줄로 합친다. */
const TOP_N = 30;
/**
 * Check Points 기준값. 회사 사정에 맞게 여기서만 조정한다.
 * 리포트 하단에 기준이 그대로 표시되므로, 바꾸면 화면 설명도 함께 바뀐다.
 */
const RULES = {
  /** 상위 10곳 매출 비중이 이 값(%) 이상이면 "집중도 높음" */
  concentrationPct: 50,
  /** 모든 기간에 거래한 고객의 매출 비중이 이 값(%) 이상이면 "기존 고객 의존" */
  coreDependencePct: 70,
  /** 신규 고객 매출이 마지막 기간 매출의 이 값(%) 미만이면 "신규 고객 아직 작음" */
  newSmallPct: 5,
  /** 마지막 기간 매출(환산)이 직전 기간 대비 이 값(%) 이상 움직이면 표시 */
  swingPct: 20,
  /** 마지막 기간이 이 비율 미만만 지났으면 환산 비교(하락 고객·매출 변동·미거래 고객)는 건너뛴다 */
  minElapsed: 0.25,
};

const COLORS = {
  navy: "#0A1F33",
  gold: "#B8862E",
  goldLight: "#E4C88A",
  steelBlue: "#3E6E96",
  up: "#2F6F52",
  down: "#9C3B3B",
  warn: "#B8862E",
  textMuted: "#4A5A66",
  faint: "#8A98A2",
  border: "#D8E0E3",
  rowAlt: "#FAFBFB",
};

const STYLE = `
  @page { size: A4 landscape; margin: 13mm 12mm 15mm; }
  * { box-sizing: border-box; }
  body { font-family:-apple-system,'Segoe UI',Pretendard,Arial,sans-serif; color:#101B26; margin:0; font-size:11.5px; background:#fff; }
  .tsc-report { max-width:1120px; margin:0 auto; }

  .masthead { background:${COLORS.navy}; color:#fff; padding:18px 24px 16px; border-radius:6px;
              display:flex; justify-content:space-between; align-items:flex-end; margin-bottom:12px; }
  .masthead h1 { font-size:21px; margin:0 0 5px; font-weight:700; letter-spacing:-.01em; }
  .masthead .eyebrow { font-size:9.5px; letter-spacing:.14em; text-transform:uppercase; color:${COLORS.goldLight}; margin-bottom:6px; }
  .masthead .meta { font-size:10px; color:#B9C7D1; line-height:1.6; }
  .masthead .meta b { color:#fff; }
  .masthead .right { text-align:right; font-size:9.5px; color:#8FA7B8; line-height:1.5; }

  .warn-banner { background:#FFF6E5; border:1px solid ${COLORS.warn}; color:#6B4E12; border-radius:5px;
                 padding:8px 12px; font-size:10px; margin-bottom:12px; }

  .kpi-grid { display:flex; gap:10px; margin-bottom:12px; }
  .kpi-grid .kpi { flex:1; }
  .kpi { background:#fff; border:1px solid ${COLORS.border}; border-top:3px solid ${COLORS.gold}; border-radius:5px; padding:10px 12px 9px; }
  .kpi.core { border-top-color:${COLORS.navy}; }
  .kpi.new { border-top-color:${COLORS.steelBlue}; }
  .kpi-label { font-size:9px; color:${COLORS.textMuted}; text-transform:uppercase; letter-spacing:.04em; font-weight:700; margin-bottom:5px; }
  .kpi-num { font-family:'Courier New',monospace; font-size:19px; font-weight:700; line-height:1; margin-bottom:5px; }
  .kpi-num.muted { color:${COLORS.faint}; }
  .kpi-note { font-size:9px; color:${COLORS.textMuted}; line-height:1.5; }

  .insight { background:${COLORS.navy}; color:#EAF0F4; border-radius:5px; padding:12px 14px; font-size:10px; line-height:1.65; margin-bottom:12px; }
  .insight .tag { font-family:'Courier New',monospace; font-size:8.5px; letter-spacing:.1em; text-transform:uppercase; color:${COLORS.goldLight}; margin-bottom:5px; display:block; }
  .insight b { color:#fff; }

  table.cust-table { width:100%; border-collapse:collapse; margin-top:6px; }
  table.cust-table thead { display:table-header-group; }
  table.cust-table th { background:${COLORS.navy}; color:#fff; font-size:8px; text-transform:uppercase; letter-spacing:.02em;
                   padding:5px 4px; text-align:center; font-weight:600; }
  table.cust-table th.rank-h { width:24px; text-align:center; }
  table.cust-table th.name-h { text-align:left; padding-left:8px; }
  table.cust-table th .hdr-sub { display:block; font-family:'Courier New',monospace; font-size:7.2px;
                                 font-weight:400; color:${COLORS.goldLight}; letter-spacing:0; margin-top:2px; }
  table.cust-table td { border-bottom:1px solid #EEF2F0; padding:3px 4px; vertical-align:middle; }
  table.cust-table tr { page-break-inside:avoid; }
  table.cust-table tr:nth-child(even) { background:${COLORS.rowAlt}; }

  td.rank { text-align:center; font-family:'Courier New',monospace; font-weight:700; color:${COLORS.faint}; font-size:8.6px; width:24px; }
  td.cname { font-size:8.6px; font-weight:700; color:#101B26; width:132px; padding-left:8px; }
  td.cname.unknown { color:${COLORS.faint}; font-style:italic; }
  a.drill-link { color:#0000FF; text-decoration:none; cursor:pointer; }
  a.drill-link:hover { text-decoration:underline; }
  .name-sub { display:flex; align-items:center; justify-content:space-between; gap:4px; margin-top:2px; }
  .name-sub .dots { margin-top:0; }

  /* 버튼 줄 — 5 Years 리포트와 같은 모양 (See Detail / Business Chart) */
  .acts { display:flex; gap:3px; margin-top:3px; }
  .acts a, .acts a.drill-link { display:inline-flex; align-items:center; gap:3px; height:14px; padding:0 6px;
           font-family:-apple-system,'Segoe UI',Pretendard,Arial,sans-serif; font-size:7.8px; font-weight:700;
           border-radius:3px; border:1px solid; text-decoration:none; cursor:pointer; line-height:1; white-space:nowrap;
           transition:background .12s, color .12s; }
  .acts a::after { content:"\\2192"; font-size:8.5px; line-height:1; transition:transform .12s; }
  .acts a:hover::after { transform:translateX(2px); }
  .acts a:hover, .acts a.drill-link:hover { text-decoration:none; }
  .act.detail a { color:#7A5A1C; border-color:#D9BE85; background:#F6EEDC; }
  .act.detail a:hover { background:${COLORS.gold}; border-color:${COLORS.gold}; color:#fff; }
  .act.chart a { color:#2E5578; border-color:#A9C0D3; background:#E4EDF5; }
  .act.chart a:hover { background:${COLORS.steelBlue}; border-color:${COLORS.steelBlue}; color:#fff; }
  .dots { margin-top:2px; }
  .dot { display:inline-block; width:4px; height:4px; border-radius:50%; margin-right:1px; }
  .dot.on { background:${COLORS.up}; }
  .dot.off { background:${COLORS.border}; }
  td.c-cell { width:66px; }
  td.c-cell.empty { text-align:center; color:#C7D0D4; font-size:8.6px; }
  td.tot-cell { width:80px; }
  .c-val { font-family:'Courier New',monospace; font-size:8.1px; font-weight:700; color:#101B26; }
  td.tot-cell .c-val { font-size:8.4px; color:${COLORS.navy}; }
  .c-bar-track { width:100%; height:3px; background:#E5EAEC; border-radius:2px; margin-top:1px; overflow:hidden; }
  .c-bar-fill { height:100%; border-radius:2px; }
  /* 음수 금액 — Others/TOTAL 줄의 글자색 규칙보다 우선 */
  table.cust-table tr td .c-val.neg { color:${COLORS.down}; }
  td.chg-cell { width:50px; text-align:center; }
  .chg { font-family:'Courier New',monospace; font-size:8.1px; font-weight:700; padding:1px 5px; border-radius:8px; display:inline-block; }
  .chg.up { background:${COLORS.up}22; color:${COLORS.up}; border:1px solid ${COLORS.up}55; }
  .chg.down { background:${COLORS.down}22; color:${COLORS.down}; border:1px solid ${COLORS.down}55; }
  .chg.new { background:${COLORS.gold}22; color:${COLORS.gold}; border:1px solid ${COLORS.gold}55; }
  .chg.flat { background:${COLORS.border}44; color:${COLORS.faint}; border:1px solid ${COLORS.border}; }

  /* Top N 밖의 고객 합계 */
  tr.others-row td { background:#F4F6F7; border-top:1px dashed ${COLORS.border}; }
  tr.others-row:nth-child(even) td { background:#F4F6F7; }
  tr.others-row td.cname { color:${COLORS.textMuted}; font-style:italic; }
  tr.others-row .c-val, tr.others-row td.tot-cell .c-val { color:${COLORS.textMuted}; }

  tr.total-row td { background:#FBF6EA; color:#101B26; font-weight:700; border-top:2px solid ${COLORS.gold}; border-bottom:none; }
  tr.total-row:nth-child(even) td { background:#FBF6EA; }
  tr.total-row td.rank { color:${COLORS.gold}; }
  tr.total-row td.cname { color:${COLORS.navy}; font-size:9px; }
  tr.total-row .c-val { color:${COLORS.navy}; }
  tr.total-row .c-bar-track { background:#E8DFC8; }

  .footnote { font-size:8.5px; color:${COLORS.faint}; margin-top:10px; line-height:1.6;
              border-top:1px solid ${COLORS.border}; padding-top:6px; display:flex; justify-content:space-between; }

  /* Check Points — 규칙에 걸린 항목만 표시 */
  .chk-head { display:flex; justify-content:space-between; align-items:baseline; margin:14px 0 6px; }
  .chk-head h3 { margin:0; font-size:11px; font-weight:800; color:${COLORS.navy}; text-transform:uppercase; letter-spacing:.04em; }
  .chk-head span { font-size:8.6px; color:${COLORS.faint}; }
  .chk-list { list-style:none; margin:0; padding:0; display:flex; flex-wrap:wrap; gap:8px; }
  .chk-item { width:calc(50% - 4px); background:#fff; border:1px solid ${COLORS.border}; border-left:3px solid ${COLORS.steelBlue};
              border-radius:5px; padding:8px 11px; page-break-inside:avoid; }
  .chk-item.warn { border-left-color:${COLORS.down}; }
  .chk-item h4 { margin:0 0 3px; font-size:9.5px; font-weight:800; color:${COLORS.navy}; }
  .chk-item.warn h4 { color:${COLORS.down}; }
  .chk-item p { margin:0; font-size:8.6px; color:#101B26; line-height:1.5; }
  .chk-item .rule { margin-top:4px; font-size:7.8px; color:${COLORS.faint}; }
  .chk-none { font-size:9px; color:${COLORS.textMuted}; background:#F4F6F7; border:1px dashed ${COLORS.border};
              border-radius:5px; padding:8px 11px; }
  .chk-rules { font-size:7.8px; color:${COLORS.faint}; margin-top:6px; line-height:1.6; }

  .state-wrap { min-height:70vh; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:16px; }
  .spinner { width:40px; height:40px; border-radius:50%; border:4px solid #eee; border-top-color:${COLORS.gold}; animation:tsc-spin 0.8s linear infinite; }
  @keyframes tsc-spin { to { transform:rotate(360deg); } }
  .state-text { color:${COLORS.faint}; font-size:13px; }
  .state-text.error { color:${COLORS.down}; }

  @media print {
    .masthead, .insight, .kpi, .chg, tr.total-row td, tr.others-row td, table.cust-table th, .c-bar-fill {
      -webkit-print-color-adjust:exact; print-color-adjust:exact;
    }
  }
`;

interface CustomerRow {
  comId: number;
  name: string;
  isUnknownName: boolean;
  /** Agent 그룹 업체인지 — Business Chart 를 Agent 기준으로 열지 정할 때 쓴다(틀려도 대시보드가 반대 기준으로 한 번 더 찾는다) */
  isAgent: boolean;
  values: number[];
  active: boolean[];
  total: number;
}

/** 표시 자릿수로 반올림(0 에서 먼 쪽, toLocaleString 과 같은 방향). -0 은 0 으로 — "-0.0%", "$-0" 방지 */
function roundTo(n: number, digits: number): number {
  const f = Math.pow(10, digits);
  return (Math.sign(n) * Math.round(Math.abs(n) * f)) / f || 0;
}
/** 천 단위 구분 + 고정 소수 자릿수 */
function fmtNum(n: number, digits: number): string {
  return roundTo(n, digits).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}
/** 음수는 "-$1,234" — 이전에는 "$-1,234" 로 나왔다 */
function fmtMoney(n: number): string {
  const r = roundTo(n, 0);
  return (r < 0 ? "-$" : "$") + fmtNum(Math.abs(r), 0);
}
function fmtPct1(n: number): string {
  return fmtNum(n, 1) + "%";
}
/** "45%" — 뒤에 % 를 다시 붙이지 않는다. */
function fmtPct0(n: number): string {
  return fmtNum(n, 0) + "%";
}
/** 고객 수 등 건수 — "1,234" */
function fmtCount(n: number): string {
  return fmtNum(n, 0);
}
/** 금액 칸 음수 표시용 클래스 */
function negCls(v: number): string {
  return roundTo(v, 0) < 0 ? " neg" : "";
}
/** 문장용 up/down — 소수 1자리 표시값 기준("down 0.0%" 방지) */
function upDown(pct: number): string {
  return roundTo(pct, 1) >= 0 ? "up" : "down";
}
function safeDiv(a: number, b: number): number {
  return b ? a / b : 0;
}
/** "the top 10 customers" / 한 명뿐이면 "the top customer" */
function topGroup(k: number): string {
  return k === 1 ? "the top customer" : `the top ${k} customers`;
}
function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
/** 단수/복수 동사 — verb(1, "drives", "drive") */
function verb(k: number, one: string, many: string): string {
  return k === 1 ? one : many;
}
/** "1 customer" / "3 customers" */
function plural(n: number, word: string): string {
  return `${fmtCount(n)} ${n === 1 ? word : word + "s"}`;
}
function toNum(v: unknown): number {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}
function pickName(r: any): string | null {
  const raw = r.comSName ?? r.ComSName ?? r.COMSNAME ?? null;
  return raw && String(raw).trim() !== "" ? String(raw).trim() : null;
}
/**
 * Business Chart 로 넘길 기간 — "YYYY-MM-DD~YYYY-MM-DD".
 * BusinessChartDashboard 의 splitPeriod 가 이 형식을 읽는다(날짜 하나만 오면 기존처럼 5년).
 */
function periodArg(fromDate: string, toDate: string): string {
  const iso = (s: string) => {
    const d = parseDate(s);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  return `${iso(fromDate)}~${iso(toDate)}`;
}
/** 쿼리 결과의 isAgent — true/1/"1"/"Y" 모두 Agent 로 본다. 값이 없으면 Customer. */
function isAgentRow(r: any): boolean {
  const v = r.isAgent ?? r.IsAgent ?? r.ISAGENT;
  return v === true || v === 1 || v === "1" || String(v).toUpperCase() === "Y" || String(v).toLowerCase() === "true";
}
function numField(r: any, key: string): number {
  const upper = key.charAt(0).toUpperCase() + key.slice(1);
  return toNum(r[key] ?? r[upper]);
}

function parseDate(s: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s ?? "").trim());
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  return isNaN(d.getTime()) ? new Date() : d;
}
function addDays(d: Date, n: number): Date {
  const r = new Date(d.getTime());
  r.setDate(r.getDate() + n);
  return r;
}
function addMonths(d: Date, n: number): Date {
  const r = new Date(d.getTime());
  r.setMonth(r.getMonth() + n);
  return r;
}
function minDate(a: Date, b: Date): Date { return a.getTime() <= b.getTime() ? a : b; }
function dayCount(from: Date, to: Date): number {
  return Math.floor((to.getTime() - from.getTime()) / 86400000) + 1;
}

function buildMonthCols(fromDate: string, toDate: string): MonthCol[] {
  const from = parseDate(fromDate);
  const to = parseDate(toDate);

  let visible = 1;
  for (let i = 1; i <= 12; i++) {
    if (addMonths(from, i - 1).getTime() <= to.getTime()) visible = i;
    else break;
  }

  const cols: MonthCol[] = [];
  for (let i = 1; i <= visible; i++) {
    const start = addMonths(from, i - 1);
    const fullTo = addDays(addMonths(start, 1), -1);
    const isLast = i === visible;
    const idx = isLast ? Array.from({ length: 12 - i + 1 }, (_, k) => i + k) : [i];
    cols.push({
      label: MONTH_NAMES[start.getMonth()],
      sub: String(start.getFullYear()),
      salesKeys: idx.map(k => `sales${k}`),
      from: start,
      to: isLast ? minDate(fullTo, to) : fullTo,
      fullTo,
    });
  }
  return cols;
}

function monthFraction(col: MonthCol): number {
  return Math.min(1, Math.max(safeDiv(dayCount(col.from, col.to), dayCount(col.from, col.fullTo)), 0.01));
}

function toRows(data: SalesCostByCustomer[], cols: MonthCol[]): CustomerRow[] {
  return data
    .map((r: any) => {
      const values = cols.map(c => c.salesKeys.reduce((s, k) => s + numField(r, k), 0));
      const active = values.map(v => v > 0);
      const total = values.reduce((s, v) => s + v, 0);
      const name = pickName(r);
      return {
        comId: toNum(r.comId ?? r.ComId),
        name: name ?? "(Unknown Customer)",
        isUnknownName: !name,
        isAgent: isAgentRow(r),
        values,
        active,
        total,
      };
    })
    .filter(r => r.total > 0);
}

function trendBadge(current: number, prior: number, fraction: number): string {
  if (prior <= 0) {
    return current > 0 ? `<span class="chg new">NEW</span>` : `<span class="chg flat">–</span>`;
  }
  const projected = safeDiv(current, fraction);
  const pct = ((projected - prior) / prior) * 100;
  // 반올림한 값으로 방향을 정한다 — -0.3% 가 "▼0%" 빨간 배지로 나오지 않게. 큰 값은 "▲1,234%"
  const r = roundTo(pct, 0);
  const cls = r >= 0 ? "up" : "down";
  const arrow = r >= 0 ? "▲" : "▼";
  return `<span class="chg ${cls}">${arrow}${fmtNum(Math.abs(r), 0)}%</span>`;
}

function buildCustomerRows(
  rows: CustomerRow[],
  lastIdx: number,
  lastFraction: number,
  channelName: string,
  fromDate: string,
  toDate: string,
  department: string
): string {
  return rows
    .map((c, idx) => {
      const rowMax = Math.max(0, ...c.values);
      const dots = c.active.map(a => `<span class="dot ${a ? "on" : "off"}"></span>`).join("");

      const cells = c.values
        .map(v => {
          if (roundTo(v, 0) === 0) return `<td class="c-cell empty">–</td>`;
          // 그 기간 합계가 마이너스(Credit 등)면 "–" 로 숨기지 않고 금액만 보여 준다 — 숨기면 Total 과 칸 합계가 안 맞는다
          if (v < 0) return `<td class="c-cell"><div class="c-val${negCls(v)}">${fmtMoney(v)}</div></td>`;
          const w = rowMax > 0 ? Math.max(1, (v / rowMax) * 100) : 0;
          return `
            <td class="c-cell">
              <div class="c-val">${fmtMoney(v)}</div>
              <div class="c-bar-track"><div class="c-bar-fill" style="width:${w.toFixed(1)}%; background:${COLORS.navy};"></div></div>
            </td>`;
        })
        .join("");

      const badge = lastIdx > 0
        ? trendBadge(c.values[lastIdx], c.values[lastIdx - 1], lastFraction)
        : `<span class="chg flat">–</span>`;

      const actions = c.isUnknownName
        ? ""
        : `<div class="acts">
            <span class="act detail">${buildBroadcastHyperlink("See Detail", channelName, [
              "CustomerGrossProfit",
              c.comId,
              fromDate,
              toDate,
              department,
              c.name,
              NEW_TAB_SLOT,  // args[6] — 클릭한 창에서 미리 연 탭 이름(팝업 차단 회피)
            ], undefined, "ifms-gp")}</span>
            <span class="act chart">${buildBroadcastHyperlink("Business Chart", channelName, [
              "BusinessChartDashboard",
              c.comId,
              c.name,
              periodArg(fromDate, toDate),   // "from~to" — 대시보드를 이 리포트와 같은 기간으로 연다
              department,
              c.isAgent ? "AGENT" : "CUSTOMER",
              NEW_TAB_SLOT,  // args[6] — 클릭한 창에서 미리 연 탭 이름(팝업 차단 회피)
              "1",
            ], undefined, "ifms-bc")}</span>
          </div>`;

      return `
        <tr>
          <td class="rank">${idx + 1}</td>
          <td class="cname${c.isUnknownName ? " unknown" : ""}">${c.name}<div class="name-sub"><div class="dots">${dots}</div></div>${actions}</td>
          ${cells}
          <td class="tot-cell"><div class="c-val${negCls(c.total)}">${fmtMoney(c.total)}</div></td>
          <td class="chg-cell">${badge}</td>
        </tr>`;
    })
    .join("");
}

/** 여러 고객을 합친 한 줄 — Others / TOTAL 에 같이 쓴다. */
function buildAggRow(
  rows: CustomerRow[],
  mCount: number,
  label: string,
  rankMark: string,
  rowCls: string,
  barColor: string,
  lastFraction: number
): string {
  const totalsByMonth = new Array(mCount).fill(0) as number[];
  rows.forEach(c => c.values.forEach((v, i) => (totalsByMonth[i] += v)));

  const grand = totalsByMonth.reduce((s, v) => s + v, 0);
  const rowMax = Math.max(0, ...totalsByMonth);
  const lastIdx = mCount - 1;

  const cells = totalsByMonth
    .map(v => {
      if (roundTo(v, 0) === 0) return `<td class="c-cell empty">–</td>`;
          // 그 기간 합계가 마이너스(Credit 등)면 "–" 로 숨기지 않고 금액만 보여 준다 — 숨기면 Total 과 칸 합계가 안 맞는다
          if (v < 0) return `<td class="c-cell"><div class="c-val${negCls(v)}">${fmtMoney(v)}</div></td>`;
      const w = rowMax > 0 ? Math.max(1, (v / rowMax) * 100) : 0;
      return `
        <td class="c-cell">
          <div class="c-val">${fmtMoney(v)}</div>
          <div class="c-bar-track"><div class="c-bar-fill" style="width:${w.toFixed(1)}%; background:${barColor};"></div></div>
        </td>`;
    })
    .join("");

  const badge = lastIdx > 0
    ? trendBadge(totalsByMonth[lastIdx], totalsByMonth[lastIdx - 1], lastFraction)
    : `<span class="chg flat">–</span>`;

  return `
    <tr class="${rowCls}">
      <td class="rank">${rankMark}</td>
      <td class="cname">${label}</td>
      ${cells}
      <td class="tot-cell"><div class="c-val${negCls(grand)}">${fmtMoney(grand)}</div></td>
      <td class="chg-cell">${badge}</td>
    </tr>`;
}

/* ────────────── Check Points ────────────── */

interface CheckPoint { level: "warn" | "info"; title: string; detail: string; rule: string; }

function escHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/** "A, B, C and 2 more" — 항목은 이미 HTML 로 만들어진 문자열. */
function listOf(items: string[], max = 5): string {
  const shown = items.slice(0, max).join(", ");
  return items.length > max ? `${shown} and ${items.length - max} more` : shown;
}

/** 고객 이름 + 하락률, 예: "ACME (▼18%)" */
function declineTag(name: string, current: number, prior: number): string {
  const pct = safeDiv(current - prior, prior) * 100;
  // 0.5% 미만 하락은 "▼0%" 대신 "▼<1%"
  const a = Math.abs(pct);
  return `${escHtml(name)} (▼${a < 0.5 ? "&lt;1" : fmtNum(a, 0)}%)`;
}

/** 규칙에 걸린 항목만 카드로 보여 준다. 걸린 게 없으면 "없음" 한 줄. 기준은 항상 아래에 적는다. */
function buildCheckPoints(items: CheckPoint[], notes: string[]): string {
  const body = items.length > 0
    ? `<ul class="chk-list">${items.map(c => `
        <li class="chk-item ${c.level}">
          <h4>${c.title}</h4>
          <p>${c.detail}</p>
          <div class="rule">Rule: ${c.rule}</div>
        </li>`).join("")}
      </ul>`
    : `<div class="chk-none">No check point was triggered for this period.</div>`;
  return `
    <div class="chk-head"><h3>Check Points</h3><span>Shown only when one of the rules below is met</span></div>
    ${body}
    <div class="chk-rules">${notes.join(" &middot; ")}</div>`;
}

function buildReportInnerHtml(
  data: SalesCostByCustomer[],
  asOfDate: Date,
  companyName: string,
  currency: string,
  salesBasis: string,
  department: string,
  channelName: string,
  fromDate: string,
  toDate: string,
  footerLeft?: string,
  footerRight?: string
): string {
  let basisText = "Total";
  switch (salesBasis.toUpperCase()) {
    case "TOTAL": basisText = "Total"; break;
    case "SALES": basisText = "Invoice Only"; break;
    case "AGENTSALES": basisText = "Db Note Only"; break;
  }

  const cols = buildMonthCols(fromDate, toDate);
  const mCount = cols.length;
  const lastIdx = mCount - 1;
  const lastCol = cols[lastIdx];
  const lastFraction = monthFraction(lastCol);
  const isPartialLast = lastCol.to.getTime() < lastCol.fullTo.getTime();
  const lastLabel = `${lastCol.label} ${lastCol.sub}`;
  // 한 달짜리 기간이면 신규/매달 거래 고객, 전월 대비를 따질 수 없다.
  const multi = mCount > 1;
  const priorLabel = multi ? `${cols[lastIdx - 1].label} ${cols[lastIdx - 1].sub}` : "";

  const rows = toRows(data, cols);
  const n = rows.length;
  const asOfStr = fmtAsOf(asOfDate);

  if (n === 0) {
    return `<div class="tsc-report"><p style="padding:24px;color:${COLORS.faint};">No customer data available.</p></div>`;
  }

  const unknownNameCount = rows.filter(r => r.isUnknownName).length;
  const warnBanner = unknownNameCount > 0
    ? `<div class="warn-banner">⚠ ${fmtCount(unknownNameCount)}개 행에서 고객명(comSName)이 비어있어 "(Unknown Customer)"로 표시됩니다.</div>`
    : "";

  const sortedDesc = [...rows].sort((a, b) => b.total - a.total);
  const grandTotal = sortedDesc.reduce((s, c) => s + c.total, 0) || 1;
  const lastMonthTotal = sortedDesc.reduce((s, c) => s + c.values[lastIdx], 0);

  const top1Pct = safeDiv(sortedDesc[0].total, grandTotal) * 100;

  const top10 = sortedDesc.slice(0, Math.min(10, n));
  const top10Pct = safeDiv(top10.reduce((s, c) => s + c.total, 0), grandTotal) * 100;

  const top30 = sortedDesc.slice(0, Math.min(30, n));
  const top30Pct = safeDiv(top30.reduce((s, c) => s + c.total, 0), grandTotal) * 100;

  const shown = sortedDesc.slice(0, TOP_N);
  const others = sortedDesc.slice(TOP_N);

  const coreRows = multi ? sortedDesc.filter(c => c.active.every(Boolean)) : [];
  const corePct = safeDiv(coreRows.reduce((s, c) => s + c.total, 0), grandTotal) * 100;

  // 기간 안에서 마지막 달에 처음 매출이 생긴 고객. 비중은 마지막 달 매출 대비로 본다.
  const newRows = multi
    ? sortedDesc.filter(c => c.active[lastIdx] && c.active.slice(0, lastIdx).every(a => !a))
    : [];
  const newRev = newRows.reduce((s, c) => s + c.values[lastIdx], 0);
  const newPct = safeDiv(newRev, lastMonthTotal) * 100;

  /* ── 마지막 기간을 직전 기간과 비교한다. 진행 중인 기간은 경과 비율로 환산하되, 너무 이르면 비교하지 않는다. */
  const lastTotal = lastMonthTotal;
  const prevTotal = multi ? sortedDesc.reduce((s, c) => s + c.values[lastIdx - 1], 0) : 0;
  const projectedLast = safeDiv(lastTotal, lastFraction);
  const compareOk = multi && lastFraction >= RULES.minElapsed;
  const changePct = compareOk && prevTotal > 0 ? ((projectedLast - prevTotal) / prevTotal) * 100 : null;
  const decliningTop10 = compareOk
    ? top10
      .filter(c => c.values[lastIdx - 1] > 0 && c.values[lastIdx] > 0 && safeDiv(c.values[lastIdx], lastFraction) < c.values[lastIdx - 1])
      // 하락 폭이 큰 고객부터. 마지막 기간 매출이 아예 없는 고객은 아래 '미거래' 항목에서 따로 보여 준다.
      .sort((a, b) => safeDiv(a.values[lastIdx], lastFraction) / a.values[lastIdx - 1] - safeDiv(b.values[lastIdx], lastFraction) / b.values[lastIdx - 1])
    : [];
  const lapsedTop30 = compareOk ? top30.filter(c => c.values[lastIdx - 1] > 0 && c.values[lastIdx] <= 0) : [];
  const projNote = isPartialLast ? ` (projected from ${Math.round(lastFraction * 100)}% of the month)` : "";

  /* ── Insight — 표와 KPI 에서 바로 확인되는 사실만 적는다 */
  const concText = n > 10
    ? `The <b>top 10 customers account for ${fmtPct1(top10Pct)}</b> of period revenue${n > 30 ? ` and the <b>top 30 for ${fmtPct1(top30Pct)}</b>` : ""}.`
    : `The largest customer accounts for <b>${fmtPct1(top1Pct)}</b> of period revenue.`;

  let bodyText: string;
  if (!multi) {
    bodyText = `The range covers a single month, so there is no earlier month to compare with.`;
  } else {
    const coreText = coreRows.length > 0
      ? `<b>${plural(coreRows.length, "customer")}</b> transacted in every month of the range (${fmtPct1(corePct)} of revenue).`
      : `No customer transacted in every month of the range.`;
    const newText = newRows.length > 0
      ? `<b>${plural(newRows.length, "customer")}</b> first appeared in ${lastLabel}, totalling ${fmtMoney(newRev)} (${fmtPct1(newPct)} of ${lastLabel} sales).`
      : `No customer first appeared in ${lastLabel}.`;
    const changeText = changePct === null
      ? ""
      : `${lastLabel} sales${isPartialLast ? ` project to about ${fmtMoney(projectedLast)}` : ` were ${fmtMoney(lastTotal)}`}, ${upDown(changePct)} <b>${fmtNum(Math.abs(changePct), 1)}%</b> on ${priorLabel}.`;
    bodyText = `${coreText} ${newText} ${changeText}`;
  }

  /* ── Check Points — 규칙에 걸린 것만 */
  const checks: CheckPoint[] = [];
  if (n > 10 && top10Pct >= RULES.concentrationPct) {
    checks.push({
      level: "warn",
      title: "High customer concentration",
      detail: `The top 10 customers account for ${fmtPct1(top10Pct)} of period revenue.`,
      rule: `top 10 share &ge; ${RULES.concentrationPct}%`,
    });
  }
  if (decliningTop10.length > 0) {
    checks.push({
      level: "warn",
      title: `Top accounts below ${priorLabel}`,
      detail: `${plural(decliningTop10.length, "top-10 customer")} ${verb(decliningTop10.length, "is", "are")} below ${priorLabel} in ${lastLabel}${projNote}: `
        + `${listOf(decliningTop10.map(c => declineTag(c.name, safeDiv(c.values[lastIdx], lastFraction), c.values[lastIdx - 1])))}.`,
      rule: `top 10 customer whose ${lastLabel} sales${isPartialLast ? ", projected," : ""} are below ${priorLabel}`,
    });
  }
  if (lapsedTop30.length > 0) {
    checks.push({
      level: "warn",
      title: `No ${lastLabel} sales${isPartialLast ? " yet" : ""} from ${plural(lapsedTop30.length, "top-30 customer")}`,
      detail: `${verb(lapsedTop30.length, "This customer", "These customers")} bought in ${priorLabel} but ${verb(lapsedTop30.length, "has", "have")} no sales in ${lastLabel}${isPartialLast ? " so far" : ""}: `
        + `${listOf(lapsedTop30.map(c => escHtml(c.name)))}.`,
      rule: `top 30 customer with ${priorLabel} sales and no ${lastLabel} sales`,
    });
  }
  if (changePct !== null && Math.abs(changePct) >= RULES.swingPct) {
    checks.push({
      level: changePct < 0 ? "warn" : "info",
      title: `Sales ${changePct < 0 ? "down" : "up"} ${fmtNum(Math.abs(changePct), 1)}% vs ${priorLabel}`,
      detail: `${lastLabel} sales of ${fmtMoney(lastTotal)}${isPartialLast ? ` project to about ${fmtMoney(projectedLast)}` : ""}, against ${fmtMoney(prevTotal)} in ${priorLabel}.`,
      rule: `change vs prior month${isPartialLast ? " (projected)" : ""} &ge; &plusmn;${RULES.swingPct}%`,
    });
  }
  if (multi && newRows.length === 0) {
    checks.push({
      level: "info",
      title: `No first-time customers in ${lastLabel}`,
      detail: `No customer had its first sale of the range in ${lastLabel}.`,
      rule: `customer with ${lastLabel} sales and none earlier in the range`,
    });
  } else if (multi && newPct < RULES.newSmallPct) {
    checks.push({
      level: "info",
      title: "New customers still small",
      detail: `${plural(newRows.length, "first-time customer")} ${verb(newRows.length, "accounts", "account")} for ${fmtMoney(newRev)}, ${fmtPct1(newPct)} of ${lastLabel} sales.`,
      rule: `new-customer share of ${lastLabel} sales &lt; ${RULES.newSmallPct}%`,
    });
  }
  if (multi && coreRows.length > 0 && corePct >= RULES.coreDependencePct) {
    checks.push({
      level: "info",
      title: "Revenue relies on every-month customers",
      detail: `${plural(coreRows.length, "customer")} active in every month ${verb(coreRows.length, "accounts", "account")} for ${fmtPct1(corePct)} of period revenue.`,
      rule: `every-month customers &ge; ${RULES.coreDependencePct}% of revenue`,
    });
  }

  const checkNotes = [`Thresholds: top 10 share &ge; ${RULES.concentrationPct}%, every-month customers &ge; ${RULES.coreDependencePct}% of revenue, `
      + `new-customer share &lt; ${RULES.newSmallPct}%, change vs prior month &ge; &plusmn;${RULES.swingPct}%`];
  if (!multi) {
    checkNotes.push(`Comparisons need at least two months in the range`);
  } else if (!compareOk) {
    checkNotes.push(`Comparisons with ${priorLabel} start once ${Math.round(RULES.minElapsed * 100)}% of ${lastLabel} has passed (now ${Math.round(lastFraction * 100)}%)`);
  }
  const checkPointsHtml = buildCheckPoints(checks, checkNotes);

  const rangeLabel = multi ? `${cols[0].label} ${cols[0].sub} &ndash; ${lastLabel}` : lastLabel;
  const insightHtml = `
    <div class="insight">
      <span class="tag">Insight</span>
      ${plural(n, "active customer")} in ${rangeLabel}. ${concText}
      ${bodyText}
    </div>`;

  /* ── 표 */
  const customerRowsHtml = buildCustomerRows(shown, lastIdx, lastFraction, channelName, fromDate, toDate, department);
  const othersRowHtml = others.length > 0
    ? buildAggRow(others, mCount, `Others (${plural(others.length, "customer")})`, "&hellip;", "others-row", COLORS.faint, lastFraction)
    : "";
  const totalRowHtml = buildAggRow(sortedDesc, mCount, `TOTAL (${fmtCount(n)})`, "Σ", "total-row", COLORS.gold, lastFraction);
  const tableTitle = others.length > 0 ? `TOP ${TOP_N} CUSTOMERS` : "ALL CUSTOMERS";
  const tableRight = others.length > 0 ? `Top ${TOP_N} of ${plural(n, "customer")}` : `${plural(n, "customer")} total`;

  return `
    <div class="tsc-report">
      <div class="masthead">
        <div>
          <div class="eyebrow">IFMS 5.0 · Business Intelligence</div>
          <h1>TOTAL SALES (Customer) - Monthly</h1>
          <div class="meta">Type: ${department} | Sales Basis: <b>${basisText}</b> | Currency: <b>${currency}</b> | Active Customers: <b>${fmtCount(n)}</b></div>
        </div>
        <div class="right">
          ${companyName}<br>
          As of <b style="color:#fff">${asOfStr}</b> &middot; (Monthly)
        </div>
      </div>

      ${warnBanner}

      <div class="kpi-grid">
        <div class="kpi">
          <div class="kpi-label">Customer Concentration (Top ${top10.length})</div>
          <div class="kpi-num">${fmtPct1(top10Pct)}</div>
          <div class="kpi-note">${cap(topGroup(top10.length))} of ${plural(n, "active customer")} ${verb(top10.length, "accounts", "account")} for ${fmtPct1(top10Pct)} of revenue</div>
        </div>
        <div class="kpi">
          <div class="kpi-label">Customer Concentration (Top ${top30.length})</div>
          <div class="kpi-num">${fmtPct1(top30Pct)}</div>
          <div class="kpi-note">${cap(topGroup(top30.length))} (${fmtPct1(safeDiv(top30.length, n) * 100)} of the base) ${verb(top30.length, "drives", "drive")} ${fmtPct0(top30Pct)} of revenue</div>
        </div>
        <div class="kpi core">
          <div class="kpi-label">Every-Month Customers</div>
          <div class="kpi-num${multi ? "" : " muted"}">${multi ? fmtCount(coreRows.length) : "–"}</div>
          <div class="kpi-note">${multi
            ? `Active in all ${mCount} months &mdash; ${fmtPct1(corePct)} of total revenue`
            : "Needs at least two months to compare"}</div>
        </div>
        <div class="kpi new">
          <div class="kpi-label">New Customers in ${lastLabel}</div>
          <div class="kpi-num${multi ? "" : " muted"}">${multi ? fmtCount(newRows.length) : "–"}</div>
          <div class="kpi-note">${!multi
            ? "Needs at least two months to compare"
            : newRows.length > 0
              ? `${fmtPct1(newPct)} of ${lastLabel} sales (${fmtMoney(newRev)})`
              : "No first-time customers this month"}</div>
        </div>
      </div>

      ${insightHtml}

      <div class="masthead" style="padding:12px 22px 10px;">
        <div><h1 style="font-size:16px; margin:0;">${tableTitle} — Monthly Sales Trend</h1></div>
        <div class="right">${tableRight}</div>
      </div>

      <table class="cust-table">
        <thead>
          <tr>
            <th class="rank-h">No.</th>
            <th class="name-h">Customer / Agent</th>
            ${cols.map(c => `<th>${c.label}<span class="hdr-sub">${c.sub}</span></th>`).join("")}
            <th>Total</th>
            <th>Trend<br>(vs prior mo)</th>
          </tr>
        </thead>
        <tbody>
          ${customerRowsHtml}
          ${othersRowHtml}
          ${totalRowHtml}
        </tbody>
      </table>
      <div style="font-size:8.3px; color:${COLORS.faint}; margin-top:4px;">
        Ranked by period total sales${others.length > 0 ? `; customers below the top ${TOP_N} are combined under Others` : ""} &middot;
        &#9679; ${plural(mCount, "dot")} = active/inactive by month (dark = transacted, light = no activity) &middot;
        bar length is relative to that row's best month
        ${multi ? `&middot; Trend% compares ${lastLabel}${isPartialLast ? " (projected to a full month)" : ""} against ${priorLabel}` : ""}
      </div>

      ${checkPointsHtml}

      <div class="footnote">
        <div>${footerLeft || `Source data: Total Sales (Customer) monthly report (${plural(n, "customer")})`}</div>
        <div>${footerRight || `${lastLabel} covers up to ${asOfStr}${isPartialLast ? " and is a partial month" : ""}${multi ? "; only the Trend% column is projected" : ""}`}</div>
      </div>
    </div>`;
}

export function openTotalSalesCustomerMonthlyLoadingWindow(windowName: string): Window | null {
  return openLoadingWindow({
    title: "TOTAL SALES (Customer) - Monthly",
    styleText: STYLE,
    windowName,
  });
}

export function renderTotalSalesCustomerMonthly(
  win: Window,
  data: SalesCostByCustomer[],
  asOfDate: Date,
  companyName: string,
  currency: string,
  salesBasis: string,
  department: string,
  channelName: string,
  fromDate: string,
  toDate: string,
  sourceText: string
): void {
  const html = buildReportInnerHtml(data, asOfDate, companyName, currency, salesBasis, department, channelName, fromDate, toDate, sourceText);
  renderReportIntoWindow(win, html);
}